import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import { useAccountType } from '../contexts/AccountTypeContext';
import { useDropdownContext } from '../contexts/DropdownContext';
import { useTheme } from '../contexts/ThemeContext';
import { useOrderContext } from '../contexts/OrderContext';
import { useSponsor } from '../contexts/SponsorContext';
import { useFullChartView } from '../contexts/FullChartViewContext';
import { usePanelPosition } from '../contexts/PanelPositionContext';
import { usePositionContext } from '../contexts/PositionContext';

import {
  Bell,
  ChevronDown,
  Settings,
  User,
  TrendingUp,
  BarChart3,
  Briefcase,
  Info,
  LogOut,
  Wallet,
  CreditCard,
  Users,
  Shield,
  Crown,
  Building2,
  Bitcoin,
} from 'lucide-react';
import AdminPanel from './AdminPanel';
import DepositModal from './DepositModal';
import WithdrawalModal from './WithdrawalModal';

const HeaderRightPanel: React.FC = () => {
  const { user, logout } = useAuth();
  const { getAccountBalance, transactions } = useAccount();
  const { isDemoAccount, accountType } = useAccountType();
  const { openPositions } = usePositionContext();
  
  // Get account-specific balance data
  const accountBalance = getAccountBalance(accountType);
  const { isOpen, toggleDropdown, closeDropdown } = useDropdownContext();
  const { theme, toggleTheme } = useTheme();
  const { showConfirmationPopup, setShowConfirmationPopup } = useOrderContext();
  const { isSponsorEnabled, toggleSponsor } = useSponsor();
  const { isFullChartView, toggleFullChartView } = useFullChartView();
  const { isPanelOnRight, togglePanelPosition } = usePanelPosition();
  const navigate = useNavigate();
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState<string>('');

  return (
    <div className="w-full md:w-64 md:min-w-64 md:max-w-64 flex items-center justify-between px-2 lg:px-4">
      {/* All icons in a single row with equal spacing */}
      <div className="flex items-center justify-between w-full space-x-1 lg:space-x-2">
        {/* Wallet Button */}
        <div className="relative group top-1">
          <button
            type="button"
            className="-m-1 p-1 lg:-m-2.5 lg:p-2.5 theme-text-quaternary hover:text-[#2AB7A6] transition-colors duration-200"
            onClick={() => toggleDropdown('wallet')}
            data-dropdown
          >
            <span className="sr-only">Wallet</span>
            <Wallet className={`h-3 w-3 lg:h-4 lg:w-4 ${isOpen('wallet') ? 'text-[#2AB7A6]' : ''}`} aria-hidden="true" />
          </button>
          
          {/* Wallet Tooltip */}
          <div className="absolute right-0 z-[10000] mt-2 px-2 py-1 theme-bg-quaternary theme-border border rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
            <span className="text-xs theme-text-quaternary">Wallet</span>
            <div className="absolute bottom-full right-2 w-0 h-0 border-l-3 border-r-3 border-b-3 border-transparent border-b-theme-bg-quaternary"></div>
          </div>
          
          {/* Wallet Details Popup */}
          {isOpen('wallet') && (
            <div
              className="fixed right-2 md:right-4 top-10 z-[10001] w-[calc(100vw-1rem)] md:w-96 rounded-md theme-bg-quaternary shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
              data-dropdown
              style={{ height: 'calc(100vh - 80px)', minHeight: 0 }}
            >
              <div className="py-4 relative">
                {/* Close Button */}
                <button
                  onClick={() => toggleDropdown('wallet')}
                  className="absolute top-2 right-2 p-1 theme-text-quaternary hover:theme-text-secondary transition-colors duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                {/* Header */}
                <div className="px-4 py-2 border-b theme-border mb-4">
                  <h3 className="text-base font-medium theme-text-primary flex items-center">
                    <Wallet className="h-4 w-4 mr-2 text-[#2AB7A6]" />
                    Wallet Details
                  </h3>
                </div>

                {/* Account Summary */}
                <div className="px-4 mb-4">
                  <div className={`rounded-lg p-4 ${theme === 'light' ? 'bg-gray-50 border border-gray-200' : 'theme-bg-secondary'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className={theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}>Account ID</span>
                      <span className={theme === 'light' ? 'text-gray-900 font-mono' : 'theme-text-secondary font-mono'}>#LTS-2024-001</span>
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <span className={theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}>Account Type</span>
                      <span className={theme === 'light' ? 'text-gray-900' : 'theme-text-secondary'}>Standard</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}>Status</span>
                      <span className="flex items-center">
                        <div className="w-2 h-2 bg-[#2AB7A6] rounded-full mr-2"></div>
                        <span className={theme === 'light' ? 'text-[#2AB7A6]' : 'text-[#2AB7A6]'}>Active</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Financial Overview */}
                <div className="px-4 mb-4">
                  <h4 className={`text-xs font-medium mb-3 ${theme === 'light' ? 'text-gray-700' : 'theme-text-secondary'}`}>Financial Overview</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className={`rounded-lg p-3 ${theme === 'light' ? 'bg-gray-50 border border-gray-200' : 'theme-bg-secondary'}`}>
                      <p className={`text-xs mb-1 ${theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}`}>Balance</p>
                      <p className={`text-xs font-bold ${theme === 'light' ? 'text-gray-900' : 'theme-text-primary'}`}>${isDemoAccount ? accountBalance.demoBalance.toLocaleString() : accountBalance.liveBalance.toLocaleString()}</p>
                    </div>
                    <div className={`rounded-lg p-3 ${theme === 'light' ? 'bg-gray-50 border border-gray-200' : 'theme-bg-secondary'}`}>
                      <p className={`text-xs mb-1 ${theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}`}>Equity</p>
                      <p className={`text-xs font-bold ${theme === 'light' ? 'text-gray-900' : 'theme-text-primary'}`}>${accountBalance.equity.toLocaleString()}</p>
                    </div>
                    <div className={`rounded-lg p-3 ${theme === 'light' ? 'bg-gray-50 border border-gray-200' : 'theme-bg-secondary'}`}>
                      <p className={`text-xs mb-1 ${theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}`}>Margin</p>
                      <p className={`text-xs font-bold ${theme === 'light' ? 'text-gray-900' : 'theme-text-primary'}`}>${accountBalance.margin.toLocaleString()}</p>
                    </div>
                    <div className={`rounded-lg p-3 ${theme === 'light' ? 'bg-gray-50 border border-gray-200' : 'theme-bg-secondary'}`}>
                      <p className={`text-xs mb-1 ${theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}`}>Free Margin</p>
                      <p className={`text-xs font-bold ${theme === 'light' ? 'text-gray-900' : 'theme-text-primary'}`}>${accountBalance.freeMargin.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="px-4 mb-4">
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => {
                        toggleDropdown('wallet');
                        setShowDepositModal(true);
                      }}
                      className={`flex-1 text-xs font-medium py-2 px-3 rounded-lg transition-colors duration-200 ${
                        theme === 'light' 
                          ? 'bg-[#2AB7A6] text-white hover:bg-[#25a595]' 
                          : 'bg-[#2AB7A6] text-white hover:bg-[#25a595]'
                      }`}
                    >
                      Deposit
                    </button>
                    <button 
                      onClick={() => toggleDropdown('wallet')}
                      className={`flex-1 text-xs font-medium py-2 px-3 rounded-lg transition-colors duration-200 ${
                        theme === 'light' 
                          ? 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200' 
                          : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      }`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>

                {/* Separator Line */}
                <div className="px-4 mb-4">
                  <div className="h-px theme-border"></div>
                </div>

                {/* Recent Transactions */}
                <div className="px-4 mb-8">
                  <h4 className={`text-xs font-medium mb-3 ${theme === 'light' ? 'text-gray-700' : 'theme-text-secondary'}`}>Recent Transactions</h4>
                  <div className={`rounded-lg p-3 ${theme === 'light' ? 'bg-gray-50 border border-gray-200' : 'theme-bg-secondary'}`}>
                    <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar" style={{ maxHeight: '14.25rem' }}>
                      {transactions.slice(0, 15).map((transaction) => (
                        <div key={transaction.id} className={`grid grid-cols-3 gap-2 text-xs py-1 border-b last:border-b-0 ${
                          theme === 'light' ? 'border-gray-200' : 'theme-border-secondary'
                        }`}>
                          <span className={theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}>{transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}</span>
                          <span className={`text-right ${transaction.type === 'deposit' ? 'text-[#2AB7A6]' : transaction.type === 'withdrawal' ? 'text-red-400' : 'text-blue-400'}`}>
                            {transaction.type === 'deposit' ? '+' : ''}${transaction.amount.toLocaleString()}
                          </span>
                          <span className={`text-right ${theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}`}>
                            {new Date(transaction.timestamp).getTime() > Date.now() - 24 * 60 * 60 * 1000 ? 
                              `${Math.floor((Date.now() - new Date(transaction.timestamp).getTime()) / (1000 * 60 * 60))} hours ago` :
                              `${Math.floor((Date.now() - new Date(transaction.timestamp).getTime()) / (1000 * 60 * 60 * 24))} days ago`
                            }
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Withdrawals Button */}
        <div className="relative group top-1">
          <button
            type="button"
            className="-m-1 p-1 lg:-m-2.5 lg:p-2.5 theme-text-quaternary hover:text-[#2AB7A6] transition-colors duration-200"
            onClick={() => toggleDropdown('withdrawals')}
            data-dropdown
          >
            <span className="sr-only">Withdrawals</span>
            <CreditCard className={`h-3 w-3 lg:h-4 lg:w-4 ${isOpen('withdrawals') ? 'text-[#2AB7A6]' : ''}`} aria-hidden="true" />
          </button>
          
          {/* Withdrawals Tooltip */}
          <div className="absolute right-0 z-50 mt-2 px-2 py-1 theme-bg-quaternary theme-border border rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
            <span className="text-xs theme-text-secondary">Withdrawals</span>
            <div className="absolute bottom-full right-2 w-0 h-0 border-l-3 border-r-3 border-b-3 border-transparent border-b-theme-bg-quaternary"></div>
          </div>

          {/* Withdrawals Details Popup */}
          {isOpen('withdrawals') && (
            <div className="fixed right-2 md:right-4 top-10 md:top-30 z-[9999] w-[calc(100vw-1rem)] md:w-96 rounded-md theme-bg-quaternary shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none" data-dropdown>
              <div className="py-4 relative">
                {/* Close Button */}
                <button
                  onClick={() => toggleDropdown('withdrawals')}
                  className="absolute top-2 right-2 p-1 theme-text-quaternary hover:theme-text-secondary transition-colors duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                {/* Header */}
                <div className="px-4 py-2 border-b theme-border mb-4">
                  <h3 className="text-base font-medium theme-text-primary flex items-center">
                    <CreditCard className="h-4 w-4 mr-2 text-[#2AB7A6]" />
                    Withdrawal Request
                  </h3>
                </div>

                {/* Available Balance */}
                <div className="px-4 mb-4">
                  <div className={`rounded-lg p-4 ${theme === 'light' ? 'bg-gray-50 border border-gray-200' : 'theme-bg-secondary'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}>Total Balance</span>
                      <span className={theme === 'light' ? 'text-gray-900' : 'theme-text-secondary'}>${accountBalance.totalBalance.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className={theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}>Used Margin</span>
                      <span className={theme === 'light' ? 'text-gray-900' : 'theme-text-secondary'}>
                        ${openPositions.length === 0 ? '0.00' : accountBalance.margin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}>Free Margin</span>
                      <span className={theme === 'light' ? 'text-gray-900' : 'theme-text-secondary'}>
                        ${openPositions.length === 0 ? accountBalance.totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : accountBalance.freeMargin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Withdrawal Amount */}
                <div className="px-4 mb-4">
                  <label className={`block text-xs font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : 'theme-text-secondary'}`}>
                    Amount
                  </label>
                  <div className="relative">
                    <span className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme === 'light' ? 'text-gray-500' : 'theme-text-quaternary'}`}>$</span>
                    <input
                      type="number"
                      value={withdrawalAmount}
                      onChange={(e) => setWithdrawalAmount(e.target.value)}
                      placeholder="0.00"
                      className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2AB7A6] focus:border-transparent ${
                        theme === 'light' 
                          ? 'bg-gray-50 border-gray-200 text-gray-700 placeholder-gray-400' 
                          : 'theme-bg-quaternary theme-border theme-text-primary placeholder-theme-text-quaternary'
                      }`}
                    />
                  </div>
                  <div className="flex space-x-2 mt-2">
                    <button className={`text-xs transition-colors duration-200 ${
                      theme === 'light' ? 'text-[#2AB7A6] hover:text-[#25a595]' : 'text-[#2AB7A6] hover:text-[#25a595]'
                    }`}>
                      Min: $50
                    </button>
                    <button className={`text-xs transition-colors duration-200 ${
                      theme === 'light' ? 'text-[#2AB7A6] hover:text-[#25a595]' : 'text-[#2AB7A6] hover:text-[#25a595]'
                    }`}>
                      Max: ${isDemoAccount ? (accountBalance.demoBalance + accountBalance.profit - (accountBalance.demoBalance * 0.1)).toLocaleString() : accountBalance.freeMargin.toLocaleString()}
                    </button>
                  </div>
                  
                  {/* Amount Validation */}
                  {withdrawalAmount && (
                    <div className="mt-2">
                      {parseFloat(withdrawalAmount) >= 50 && parseFloat(withdrawalAmount) <= (isDemoAccount ? (accountBalance.demoBalance + accountBalance.profit - (accountBalance.demoBalance * 0.1)) : accountBalance.freeMargin) ? (
                        <span className="text-xs text-[#2AB7A6]">✓ Valid amount</span>
                      ) : parseFloat(withdrawalAmount) < 50 ? (
                        <span className="text-xs text-red-400">✗ Minimum amount is $50</span>
                      ) : (
                        <span className="text-xs text-red-400">✗ Maximum amount is ${isDemoAccount ? (accountBalance.demoBalance + accountBalance.profit - (accountBalance.demoBalance * 0.1)).toLocaleString() : accountBalance.freeMargin.toLocaleString()}</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Withdrawal Method */}
                <div className="px-4 mb-4">
                  <label className={`block text-xs font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : 'theme-text-secondary'}`}>
                    Withdrawal Method
                  </label>
                  <div className={`rounded-lg p-4 pe-0 ${theme === 'light' ? 'bg-gray-50 border border-gray-200' : 'theme-bg-secondary'}`}>
                    <div className="max-h-24 overflow-y-auto space-y-3 pr-2 custom-scrollbar pt-1">
                      <div className={`w-full p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                        false ? 'border-[#2AB7A6] bg-gray-700' : theme === 'light' ? 'border-gray-300' : 'border-gray-600'
                      }`}>
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                            <Building2 className="h-4 w-4 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="text-xs theme-text-primary font-medium">Bank Transfer</div>
                            <div className="text-xs theme-text-quaternary">Direct bank transfer</div>
                            <div className="flex items-center justify-between mt-1 text-xs">
                              <span className="theme-text-quaternary">Processing: 1-3 business days</span>
                              <span className="theme-text-quaternary">Fee: $15</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className={`w-full p-3 rounded-lg border-2 transition-all duration-200 text-left ${
                        false ? 'border-[#2AB7A6] bg-gray-700' : 'border-gray-600'
                      }`}>
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                            <CreditCard className="h-4 w-4 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="text-xs theme-text-primary font-medium">PayPal</div>
                            <div className="text-xs theme-text-quaternary">PayPal account</div>
                            <div className="flex items-center justify-between mt-1 text-xs">
                              <span className="theme-text-quaternary">Processing: 1-2 business days</span>
                              <span className="theme-text-quaternary">Fee: $10</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className={`w-full p-3 rounded-lg border-2 transition-all duration-200 text-left ${
                        false ? 'border-[#2AB7A6] bg-gray-700' : 'border-gray-600'
                      }`}>
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-yellow-600 rounded-lg flex items-center justify-center">
                            <Bitcoin className="h-4 w-4 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="text-xs theme-text-primary font-medium">Cryptocurrency</div>
                            <div className="text-xs theme-text-quaternary">Bitcoin, Ethereum, etc.</div>
                            <div className="flex items-center justify-between mt-1 text-xs">
                              <span className="theme-text-quaternary">Processing: 1-24 hours</span>
                              <span className="theme-text-quaternary">Fee: $5</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Processing Time & Fees */}
                <div className="px-4 mb-4">
                  <div className={`rounded-lg p-3 ${theme === 'light' ? 'bg-gray-50 border border-gray-200' : 'theme-bg-secondary'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}>Processing Time</span>
                      <span className={theme === 'light' ? 'text-gray-900' : 'theme-text-secondary'}>1-3 business days</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}>Processing Fee</span>
                      <span className={theme === 'light' ? 'text-gray-900' : 'theme-text-secondary'}>$15.00</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="px-4">
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => {
                        toggleDropdown('withdrawals');
                        setShowWithdrawalModal(true);
                      }}
                      disabled={!withdrawalAmount || parseFloat(withdrawalAmount) < 50 || parseFloat(withdrawalAmount) > (isDemoAccount ? (accountBalance.demoBalance + accountBalance.profit - (accountBalance.demoBalance * 0.1)) : accountBalance.freeMargin)}
                      className={`flex-1 text-xs font-medium py-2 px-3 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                        theme === 'light' 
                          ? 'bg-[#2AB7A6] text-white hover:bg-[#25a595]' 
                          : 'bg-[#2AB7A6] text-white hover:bg-[#25a595]'
                      }`}
                    >
                      Submit Withdrawal
                    </button>
                    <button 
                      onClick={() => toggleDropdown('withdrawals')}
                      className={`flex-1 text-xs font-medium py-2 px-3 rounded-lg transition-colors duration-200 ${
                        theme === 'light' 
                          ? 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200' 
                          : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      }`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>

                {/* Recent Withdrawals */}
                <div className="px-4 mt-4">
                  <h4 className={`text-xs font-medium mb-3 ${theme === 'light' ? 'text-gray-700' : 'theme-text-secondary'}`}>Recent Withdrawals</h4>
                  <div className={`rounded-lg p-3 ${theme === 'light' ? 'bg-gray-50 border border-gray-200' : 'theme-bg-secondary'}`}>
                    <div className="space-y-2 max-h-24 overflow-y-auto pr-2 custom-scrollbar">
                      <div className={`grid grid-cols-3 gap-2 text-xs py-1 border-b last:border-b-0 ${
                        theme === 'light' ? 'border-gray-200' : 'theme-border'
                      }`}>
                        <span className={theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}>$2,500.00</span>
                        <span className="theme-text-yellow-400 text-center">Processing</span>
                        <span className={`text-right ${theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}`}>1 day ago</span>
                      </div>
                      <div className={`grid grid-cols-3 gap-2 text-xs py-1 border-b last:border-b-0 ${
                        theme === 'light' ? 'border-gray-200' : 'theme-border'
                      }`}>
                        <span className={theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}>$1,000.00</span>
                        <span className="text-[#2AB7A6] text-center">Completed</span>
                        <span className={`text-right ${theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}`}>3 days ago</span>
                      </div>
                      <div className={`grid grid-cols-3 gap-2 text-xs py-1 border-b last:border-b-0 ${
                        theme === 'light' ? 'border-gray-200' : 'theme-border'
                      }`}>
                        <span className={theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}>$500.00</span>
                        <span className="text-[#2AB7A6] text-center">Completed</span>
                        <span className={`text-right ${theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}`}>5 days ago</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Settings Button */}
        <div className="relative group top-1">
          <button
            type="button"
            className="-m-1 p-1 lg:-m-2.5 lg:p-2.5 theme-text-quaternary hover:text-[#2AB7A6] transition-colors duration-200"
            onClick={() => toggleDropdown('tradingOptions')}
            data-dropdown
          >
            <span className="sr-only">Trading Options</span>
            <Settings className={`h-3 w-3 lg:h-4 lg:w-4 ${isOpen('tradingOptions') ? 'text-[#2AB7A6]' : ''}`} aria-hidden="true" />
          </button>
          
          {/* Settings Tooltip */}
          <div className="absolute right-0 z-50 mt-2 px-2 py-1 theme-bg-quaternary theme-border border rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
            <span className="text-xs theme-text-quaternary">Settings</span>
            <div className="absolute bottom-full right-2 w-0 h-0 border-l-3 border-r-3 border-b-3 border-transparent border-b-theme-bg-quaternary"></div>
          </div>
          
          {/* Trading Options dropdown */}
          {isOpen('tradingOptions') && (
            <div className="absolute right-0 z-[9999] mt-2 w-48 md:w-48 origin-top-right rounded-md theme-bg-quaternary shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none" data-dropdown>
              <div className="py-1 relative">
                {/* Close Button */}
                <button
                  onClick={() => toggleDropdown('tradingOptions')}
                  className="absolute top-2 right-2 p-1 theme-text-quaternary hover:theme-text-secondary transition-colors duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                {/* Header */}
                <div className="px-4 py-2 border-b theme-border">
                  <h3 className="text-sm font-medium theme-text-primary text-xs text-gray-600">
                    Dashboard & Options
                  </h3>
                </div>
                <div className="px-4 py-2">
                  <button
                    onClick={() => {
                      closeDropdown();
                      navigate('/miai');
                    }}
                    className={`flex items-center w-full px-4 py-1  my-2 text-xs transition-colors duration-200 rounded-lg ${
                      window.location.pathname === '/miai'
                        ? 'bg-[#2AB7A6] text-black'
                        : 'theme-text-secondary hover:theme-bg-quaternary'
                    }`}
                  >
                    <TrendingUp className="h-4 w-4 mr-3" />
                    Dashboard
                  </button>
                  <button
                    onClick={() => {
                      closeDropdown();
                      navigate('/analytics');
                    }}
                    className={`flex items-center w-full px-4 py-1 my-2 text-xs transition-colors duration-200 rounded-lg ${
                      window.location.pathname === '/analytics'
                        ? 'bg-[#2AB7A6] text-black'
                        : 'theme-text-secondary hover:theme-bg-quaternary'
                    }`}
                  >
                    <BarChart3 className="h-4 w-4 mr-3" />
                    Analytics
                  </button>
                  <button
                    onClick={() => {
                      closeDropdown();
                      navigate('/portfolio');
                    }}
                    className={`flex items-center w-full px-4 py-1 my-2 text-xs transition-colors duration-200 rounded-lg ${
                      window.location.pathname === '/portfolio'
                        ? 'bg-[#2AB7A6] text-black'
                        : 'theme-text-secondary hover:theme-bg-quaternary'
                    }`}
                  >
                    <Briefcase className="h-4 w-3 mr-3" />
                    Portfolio
                  </button>
                  <button
                    onClick={() => {
                      closeDropdown();
                      navigate('/settings');
                    }}
                    className={`flex items-center w-full px-4 py-1 my-2 text-xs transition-colors duration-200 rounded-lg ${
                      window.location.pathname === '/settings'
                        ? 'bg-[#2AB7A6] text-black'
                        : 'theme-text-secondary hover:theme-bg-quaternary'
                    }`}
                  >
                    <Settings className="h-4 w-4 mr-3" />
                    Settings
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="relative group top-1">
          <button
            type="button"
            className="-m-1 p-1 lg:-m-2.5 lg:p-2.5 theme-text-quaternary hover:text-[#2AB7A6] transition-colors duration-200"
            onClick={() => toggleDropdown('notifications')}
            data-dropdown
          >
            <span className="sr-only">View notifications</span>
            <Bell className={`h-3 w-3 lg:h-4 lg:w-4 ${isOpen('notifications') ? 'text-[#2AB7A6]' : ''}`} aria-hidden="true" />
          </button>
          
          {/* Notifications Tooltip */}
          <div className="absolute right-0 z-[10000] mt-2 px-2 py-1 theme-bg-quaternary theme-border border rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
            <span className="text-xs theme-text-quaternary">Notifications</span>
            <div className="absolute bottom-full right-2 w-0 h-0 border-l-3 border-r-3 border-b-3 border-transparent border-b-theme-bg-quaternary"></div>
          </div>
          
          {/* Notifications dropdown */}
          {isOpen('notifications') && (
            <div className="fixed right-2 md:right-4 top-10 z-[10001] w-[calc(100vw-1rem)] md:w-80 rounded-md theme-bg-quaternary shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none" data-dropdown>
              <div className="py-2 relative">
                {/* Close Button */}
                <button
                  onClick={() => toggleDropdown('notifications')}
                  className="absolute top-2 right-2 p-1 theme-text-quaternary hover:theme-text-secondary transition-colors duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                {/* Header */}
                <div className="px-2 py-2 border-b theme-border">
                  <h3 className="text-sm font-medium theme-text-primary">
                    Notifications
                  </h3>
                </div>
                <div className="max-h-[calc(100vh-100px)] overflow-y-auto">
                  {/* Trade Alert */}
                  <div className="px-4 py-3 border-b theme-border hover:theme-bg-quinary transition-colors duration-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs theme-text-secondary font-bold">Trade Alert</span>
                          <span className="text-xs theme-text-quaternary">2 min ago</span>
                        </div>
                        <p className="text-xs theme-text-quaternary font-normal">EURUSD position closed with +$42.00 profit</p>
                      </div>
                    </div>
                  </div>

                  {/* System Message */}
                  <div className="px-4 py-3 border-b theme-border hover:theme-bg-quinary transition-colors duration-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs theme-text-secondary font-bold">System</span>
                          <span className="text-xs theme-text-quaternary">15 min ago</span>
                        </div>
                        <p className="text-xs theme-text-quaternary font-normal">Market data connection restored successfully</p>
                      </div>
                    </div>
                  </div>

                  {/* Account Update */}
                  <div className="px-4 py-3 border-b theme-border hover:theme-bg-quinary transition-colors duration-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs theme-text-secondary font-bold">Account</span>
                          <span className="text-xs theme-text-quaternary">1 hour ago</span>
                        </div>
                        <p className="text-xs theme-text-quaternary font-normal">Margin level increased to 1000%</p>
                      </div>
                    </div>
                  </div>

                  {/* Price Alert */}
                  <div className="px-4 py-3 border-b theme-border hover:theme-bg-quinary transition-colors duration-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs theme-text-secondary font-bold">Price Alert</span>
                          <span className="text-xs theme-text-quaternary">2 hours ago</span>
                        </div>
                        <p className="text-xs theme-text-quaternary font-normal">BTC reached your target price of $45,200</p>
                      </div>
                    </div>
                  </div>

                  {/* Risk Warning */}
                  <div className="px-4 py-3 border-b theme-border hover:theme-bg-quinary transition-colors duration-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs theme-text-secondary font-bold">Risk Warning</span>
                          <span className="text-xs theme-text-quaternary">3 hours ago</span>
                        </div>
                        <p className="text-xs theme-text-quaternary font-normal">GBPUSD position approaching stop loss</p>
                      </div>
                    </div>
                  </div>

                  {/* News Update */}
                  <div className="px-4 py-3 border-b theme-border hover:theme-bg-quinary transition-colors duration-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs theme-text-secondary font-bold">News</span>
                          <span className="text-xs theme-text-quaternary">4 hours ago</span>
                        </div>
                        <p className="text-xs theme-text-quaternary font-normal">Fed announces interest rate decision</p>
                      </div>
                    </div>
                  </div>

                  {/* Deposit Confirmation */}
                  <div className="px-4 py-3 border-b theme-border hover:theme-bg-quinary transition-colors duration-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs theme-text-secondary font-bold">Deposit</span>
                          <span className="text-xs theme-text-quaternary">5 hours ago</span>
                        </div>
                        <p className="text-xs theme-text-quaternary font-normal">$5,000 deposit confirmed to your account</p>
                      </div>
                    </div>
                  </div>

                  {/* Withdrawal Request */}
                  <div className="px-4 py-3 border-b theme-border hover:theme-bg-quinary transition-colors duration-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs theme-text-secondary font-bold">Withdrawal</span>
                          <span className="text-xs theme-text-quaternary">6 hours ago</span>
                        </div>
                        <p className="text-xs theme-text-quaternary font-normal">$2,500 withdrawal request submitted</p>
                      </div>
                    </div>
                  </div>

                  {/* Market Analysis */}
                  <div className="px-4 py-3 border-b theme-border hover:theme-bg-quinary transition-colors duration-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs theme-text-secondary font-bold">Analysis</span>
                          <span className="text-xs theme-text-quaternary">8 hours ago</span>
                        </div>
                        <p className="text-xs theme-text-quaternary font-normal">Weekly market analysis report available</p>
                      </div>
                    </div>
                  </div>

                  {/* Maintenance Alert */}
                  <div className="px-4 py-3 border-b theme-border hover:theme-bg-quinary transition-colors duration-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs theme-text-secondary font-bold">Maintenance</span>
                          <span className="text-xs theme-text-quaternary">12 hours ago</span>
                        </div>
                        <p className="text-xs theme-text-quaternary font-normal">Scheduled maintenance completed successfully</p>
                      </div>
                    </div>
                  </div>

                  {/* Economic Calendar */}
                  <div className="px-4 py-3 hover:theme-bg-quinary transition-colors duration-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs theme-text-secondary font-bold">Calendar</span>
                          <span className="text-xs theme-text-quaternary">1 day ago</span>
                        </div>
                        <p className="text-xs theme-text-quaternary font-normal">New economic events added to calendar</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Risk Warning Info Icon */}
        <div className="relative group top-1">
          <button className="-m-1 p-1 lg:-m-2.5 lg:p-2.5 theme-text-quaternary hover:text-[#2AB7A6] transition-colors duration-200">
            <Info className="h-3 w-3 lg:h-4 lg:w-4" />
          </button>

          {/* Risk Warning Tooltip */}
          <div className="absolute right-0 top-full mt-2 px-4 py-3 theme-bg-quaternary theme-border border rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[10000] min-w-[280px]">
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <span className="text-sm font-medium text-red-500">
                  Risk Warning
                </span>
              </div>
              <p className="text-xs theme-text-secondary leading-relaxed p-4 py-2">
                Trading involves substantial risk of loss.
                <br />
                Only trade with capital you can afford to lose.
              </p>
            </div>
            <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-700"></div>
          </div>
        </div>

        {/* User menu */}
        <div className="relative group top-0">
          <button
            type="button"
            className="flex items-center space-x-1 lg:space-x-2 text-sm rounded-full focus:outline-none"
            onClick={() => toggleDropdown('userMenu')}
            data-dropdown
          >
            <div className="h-4 w-4 lg:h-5 lg:w-5 bg-[#2AB7A6] rounded-full flex items-center justify-center">
              <User className="h-2.5 w-2.5 lg:h-3 lg:w-3 text-white" />
            </div>
            {/* <ChevronDown className={`hidden lg:block h-2.5 w-2.5 lg:h-4 lg:w-4 ${isOpen('userMenu') ? 'text-[#2AB7A6]' : 'theme-text-quaternary'}`} /> */}
          </button>
          
          {/* User dropdown */}
          {isOpen('userMenu') && (
            <div className="absolute right-0 z-[10001] mt-2 w-48 md:w-48 origin-top-right rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none transition-colors duration-200" 
                 style={{
                   backgroundColor: theme === 'dark' ? '#374151' : '#ffffff',
                   border: `1px solid ${theme === 'dark' ? '#4b5563' : '#f3f3f3'}`
                 }}
                 data-dropdown>
              <div className="py-1 relative">
                {/* Close Button */}
                <button
                  onClick={() => toggleDropdown('userMenu')}
                  className="absolute top-2 right-2 p-1 transition-colors duration-200"
                  style={{
                    color: theme === 'dark' ? '#9ca3af' : '#6b7280'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = theme === 'dark' ? '#e5e7eb' : '#374151';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = theme === 'dark' ? '#9ca3af' : '#6b7280';
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                {/* Header */}
                <div className="px-4 py-2 border-b" style={{ borderColor: theme === 'dark' ? '#4b5563' : '#f3f3f3' }}>
                  <p className="text-sm font-medium" style={{ color: theme === 'dark' ? '#f9fafb' : '#111827' }}>
                    {user?.displayName || 'User'}
                  </p>
                  <p className="text-xs" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                    {user?.email || 'user@example.com'}
                  </p>
                  <div className="flex items-center space-x-2 mt-1 py-2">
                    {user?.role === 'owner' && (
                      <span className="text-xs text-yellow-500 flex items-center">
                        <Crown className="h-3 w-3 mr-1 text-yellow-500" />
                        Owner
                      </span>
                    )}
                    {user?.role === 'admin' && (
                      <span className="text-xs theme-text-blue-500 flex items-center">
                        <Shield className="h-3 w-3 mr-1" />
                        Admin
                      </span>
                    )}
                    {user?.role === 'trader' && (
                      <span className="text-xs text-[#2AB7A6] flex items-center">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        Trader
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Admin Menu Button - Only show for admin/owner */}
                {(user?.role === 'admin' || user?.role === 'owner') && (
                  <button
                    onClick={() => {
                      closeDropdown();
                      setShowAdminPanel(true);
                    }}
                    className="flex items-center w-full px-4 py-2 text-xs transition-colors duration-200 border-b my-1"
                    style={{
                      color: theme === 'dark' ? '#e1e1e1' : '#374151',
                      borderColor: theme === 'dark' ? '#4b5563' : '#f3f3f3'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme === 'dark' ? '#4b5563' : '#f3f4f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Users className="h-4 w-4 mr-3" />
                    Manage Users
                  </button>
                )}
                
                {/* Account Details Button */}
                <button
                  onClick={() => {
                    closeDropdown();
                    navigate('/account-details');
                  }}
                  className="flex items-center w-full px-4 py-2 text-xs transition-colors duration-200 border-b my-1"
                  style={{
                    color: theme === 'dark' ? '#e5e7eb' : '#374151',
                    borderColor: theme === 'dark' ? '#4b5563' : '#f3f3f3'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme === 'dark' ? '#4b5563' : '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <User className="h-4 w-4 mr-3" />
                  Account Details
                </button>
                
                {/* Theme Toggle */}
                <div className="flex items-center justify-between w-full px-4 py-2 text-xs transition-colors duration-200 border-b my-1"
                     style={{
                       color: theme === 'dark' ? '#e5e7eb' : '#374151',
                       borderColor: theme === 'dark' ? '#4b5563' : '#f3f3f3'
                     }}
                     onMouseEnter={(e) => {
                       e.currentTarget.style.backgroundColor = theme === 'dark' ? '#4b5563' : '#f3f4f6';
                     }}
                     onMouseLeave={(e) => {
                       e.currentTarget.style.backgroundColor = 'transparent';
                     }}>
                  <div className="flex items-center">
                    {theme === 'dark' ? (
                      <svg className="h-4 w-4 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414l.707.707zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                      </svg>
                    )}
                    <span>Theme</span>
                  </div>
                  <button
                    onClick={toggleTheme}
                    className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                      theme === 'dark' ? 'bg-[#2AB7A6]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-200 ${
                        theme === 'dark' ? 'translate-x-[17px]' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
                
                {/* Buy/Sell Confirm Toggle */}
                <div className="flex items-center justify-between w-full px-4 py-2 text-xs transition-colors duration-200 border-b my-1"
                     style={{
                       color: theme === 'dark' ? '#e5e7eb' : '#374151',
                       borderColor: theme === 'dark' ? '#4b5563' : '#f3f3f3'
                     }}
                     onMouseEnter={(e) => {
                       e.currentTarget.style.backgroundColor = theme === 'dark' ? '#4b5563' : '#f3f4f6';
                     }}
                     onMouseLeave={(e) => {
                       e.currentTarget.style.backgroundColor = 'transparent';
                     }}
                     title="Toggle confirmation popup for buy/sell orders">
                  <div className="flex items-center">
                    <svg className="h-4 w-4 mr-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 22 22">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Order Confirm</span>
                  </div>
                  <button
                    onClick={() => setShowConfirmationPopup(!showConfirmationPopup)}
                    className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                      showConfirmationPopup ? 'bg-[#2AB7A6]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-200 ${
                        showConfirmationPopup ? 'translate-x-[17px]' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
                
                {/* Sponsor Enable Toggle - Hidden when full chart view is enabled */}
                {!isFullChartView && (
                  <div className="flex items-center justify-between w-full px-4 py-2 text-xs transition-colors duration-200 border-b my-1"
                       style={{
                         color: theme === 'dark' ? '#e5e7eb' : '#374151',
                         borderColor: theme === 'dark' ? '#4b5563' : '#f3f3f3'
                       }}
                       onMouseEnter={(e) => {
                         e.currentTarget.style.backgroundColor = theme === 'dark' ? '#4b5563' : '#f3f4f6';
                       }}
                       onMouseLeave={(e) => {
                         e.currentTarget.style.backgroundColor = 'transparent';
                       }}
                       title="Toggle sponsored trade visibility">
                    <div className="flex items-center">
                      <svg className="h-4 w-4 mr-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2M9 8h6m-6 4h6m-6 4h6" />
                      </svg>
                      <span>Sponsors</span>
                    </div>
                    <button
                      onClick={toggleSponsor}
                      className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                        isSponsorEnabled ? 'bg-[#2AB7A6]' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-200 ${
                          isSponsorEnabled ? 'translate-x-[17px]' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                )}
                
                {/* Full Chart View Toggle */}
                <div className="flex items-center justify-between w-full px-4 py-2 text-xs transition-colors duration-200 border-b my-1"
                     style={{
                       color: theme === 'dark' ? '#e5e7eb' : '#374151',
                       borderColor: theme === 'dark' ? '#4b5563' : '#f3f3f3'
                     }}
                     onMouseEnter={(e) => {
                       e.currentTarget.style.backgroundColor = theme === 'dark' ? '#4b5563' : '#f3f4f6';
                     }}
                     onMouseLeave={(e) => {
                       e.currentTarget.style.backgroundColor = 'transparent';
                     }}
                     title="Toggle full chart view - hides left and right panels">
                  <div className="flex items-center">
                    <svg className="h-4 w-4 mr-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                    <span>Full Chart View</span>
                  </div>
                  <button
                    onClick={toggleFullChartView}
                    className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                      isFullChartView ? 'bg-[#2AB7A6]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-200 ${
                        isFullChartView ? 'translate-x-[17px]' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
                
                {/* Panel Position Toggle - Only show when Full Chart View is disabled */}
                {!isFullChartView && (
                  <div className="flex items-center justify-between w-full px-4 py-2 text-xs transition-colors duration-200 border-b my-1"
                       style={{
                         color: theme === 'dark' ? '#e5e7eb' : '#374151',
                         borderColor: theme === 'dark' ? '#4b5563' : '#f3f3f3'
                       }}
                       onMouseEnter={(e) => {
                         e.currentTarget.style.backgroundColor = theme === 'dark' ? '#4b5563' : '#f3f4f6';
                       }}
                       onMouseLeave={(e) => {
                         e.currentTarget.style.backgroundColor = 'transparent';
                       }}
                       title="Toggle panel position - move left panel to right side">
                    <div className="flex items-center">
                      <svg className="h-4 w-4 mr-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                      <span>Panel in Right</span>
                    </div>
                    <button
                      onClick={togglePanelPosition}
                      className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                        isPanelOnRight ? 'bg-[#2AB7A6]' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-200 ${
                          isPanelOnRight ? 'translate-x-[17px]' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                )}
                
                <button
                  onClick={() => {
                    try {
                      closeDropdown();
                      logout();
                    } catch (error) {
                      // Fallback logout
                      localStorage.removeItem('token');
                      localStorage.removeItem('user');
                      window.location.href = '/login';
                    }
                  }}
                  className="flex items-center w-full px-4 py-2 my-2 text-xs transition-colors duration-200 text-red-500 hover:text-red-600"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme === 'dark' ? '#4b5563' : '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <LogOut className="h-4 w-4 mr-3 text-red-500" />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Admin Panel */}
      <AdminPanel 
        isOpen={showAdminPanel} 
        onClose={() => setShowAdminPanel(false)} 
      />
      
      {/* Deposit Modal */}
      <DepositModal 
        isOpen={showDepositModal} 
        onClose={() => setShowDepositModal(false)} 
      />
      
      {/* Withdrawal Modal */}
      <WithdrawalModal 
        isOpen={showWithdrawalModal} 
        onClose={() => {
          setShowWithdrawalModal(false);
          setWithdrawalAmount('');
        }} 
        preFilledAmount={withdrawalAmount}
      />
    </div>
  );
};

export default HeaderRightPanel;
